import { GearItem, Rarity } from "@highmarch/domain";
import { feedCandidates, feedItems, feedPreview, itemCard } from "@highmarch/app";
import { audio } from "../../audio/audio";
import { RARITY_LABEL } from "../gearUi";
import { toast } from "../toast";
import type { ArmouryCtx } from "./host";
import { holdButton } from "./hold";
import { btn, essImg, gainEl, gearDisc, h, ico, newSince, refusal } from "./itemBits";
import { closeUndo } from "./itemActions";
import { closeButton, mountPanel, onRefresh, refreshPanels } from "./itemPanels";
import { feedDefaults } from "./forgeModel";
import { gearTile } from "./tile";

/**
 * Feed (gear v2, docs/GEAR_V2.md §3): spare items melt into Forge Essence (and their gold) and the target is raised as
 * far as that goes, up to its cap. The panel picks the spares that raise it furthest without waste (junk first, then
 * the weakest), tap a tile to keep it, see the level it reaches, and hold to feed. It never offers a worn, locked, NEW
 * or Mythic item; Epic and Legendary spares only after an explicit "Include Epic+" confirmation.
 */

export const FEED_HOLD_MS = 1000;

export interface FeedResult { readonly from: number; readonly to: number }

export function openFeed(ctx: ArmouryCtx, target: GearItem, onFed: (r: FeedResult) => void): void {
  const profile = ctx.profile, B = ctx.B;
  const panel = mountPanel(ctx, "afeed");
  const root = panel.root;
  root.classList.add("ajunk");                       // Clear junk's navy plate, chips, grid and hold row (armouryItems.css)
  let epic = false;
  let confirming = false;
  const opts = () => ({ roster: ctx.scope.roster, seenUpTo: newSince(ctx), confirmEpic: epic });
  let picked = new Set(feedDefaults(profile, target, opts()));

  const render = () => {
    const t = profile.findGear(target.id);
    if (t === null) { panel.close(); return; }
    const view = itemCard(profile, t);
    const cands = feedCandidates(profile, t, opts());
    for (const id of [...picked]) if (!cands.some((c) => c.id === id)) picked.delete(id);
    const plan = picked.size > 0 ? feedPreview(profile, t.id, [...picked], epic) : null;
    const p = typeof plan === "object" && plan !== null ? plan : null;
    const to = p?.toLevel ?? t.level;

    const fill = (lv: number) => (view.cap <= 1 ? 1 : Math.max(0, Math.min(1, (lv - 1) / (view.cap - 1))));
    const reach = h("i", "max"), now = h("i", "now");
    reach.style.transform = `scaleX(${fill(to)})`;
    now.style.transform = `scaleX(${fill(t.level)})`;
    const bar = h("span", "lbar", reach, now);
    const tgt = h("div", "ftgt", gearDisc(B, t, "adisc sm"), h("div", "mid",
      h("b", "disp", to > t.level ? `Lv ${t.level} → ` : `Lv ${t.level}`, to > t.level ? h("em", "", `Lv ${to}`) : null, h("small", "", ` / ${view.cap}`)), bar,
      h("small", "", p === null ? "Pick spares to melt into essence" : to >= view.cap ? "Reaches its cap" : `${p.essence + profile.forge.essence - p.spent} essence left after`)));

    const epicChip = btn(`cchip epic${epic ? " on" : ""}`, h("i", "rdot r3"), "Include Epic+");
    epicChip.setAttribute("aria-pressed", String(epic));
    epicChip.addEventListener("click", () => { if (epic) { epic = false; picked = new Set(feedDefaults(profile, t, opts())); render(); } else confirmEpic(); });

    const grid = h("div", "jgrid");
    for (const it of cands) {
      const on = picked.has(it.id);
      const cell = h("div", `jpick${on ? " picked" : " kept"}${it.rarity >= Rarity.Epic ? " epic" : ""}`, gearTile(ctx, it, { size: "m" }), on ? h("span", "chk", "✓") : null);
      cell.setAttribute("role", "checkbox");
      cell.setAttribute("aria-checked", String(on));
      cell.addEventListener("click", () => { if (picked.has(it.id)) picked.delete(it.id); else picked.add(it.id); render(); });
      grid.append(cell);
    }
    if (cands.length === 0) grid.append(h("div", "jnone", h("p", "", "No spares to feed: worn, locked, new and Mythic items never go in.")));

    const enough = p === null && view.raise.affordableLevel >= view.cap && t.level < view.cap;
    const summary = h("div", "gain", h("small", "", p === null ? (typeof plan === "string" ? refusal(plan) : enough ? "Your essence already reaches its cap: use Raise max" : "Tap spares to pick them") : `${p.fodder.length} spare${p.fodder.length === 1 ? "" : "s"} melt into`),
      p !== null ? gainEl(p.gold, "again g big", p.essence) : null,
      p !== null && p.spent > 0 ? h("small", "pays", ico("bolt", 11), `raising it ${to - t.level} level${to - t.level === 1 ? "" : "s"} for `, essImg(), String(p.spent)) : null);
    const hold = holdButton([h("span", "disp", `Feed ${picked.size}`), h("small", "", "hold to confirm")], {
      ms: FEED_HOLD_MS, className: "abtn gold go", disabled: p === null, label: `Hold to feed ${picked.size} items into it`,
      onDone: () => doFeed(t),
      onTap: () => { if (p !== null) toast(ctx.toastParent, "Hold the button to feed"); },
    });
    root.replaceChildren(
      h("div", "jt", h("b", "disp", "Feed"), h("small", "", "Spares melt into Forge Essence that raises this item. Never worn, locked, new or Mythic."), closeButton(panel.close)),
      tgt, h("div", "ceil", epicChip, h("span", "cnote", `${cands.length} spare${cands.length === 1 ? "" : "s"}`)), grid, h("div", "go", summary, hold.el));
  };

  function confirmEpic(): void {
    if (confirming) return;
    confirming = true;
    const keep = btn("abtn parch", h("span", "disp", "Not those"));
    const go = btn("abtn dark", h("span", "disp", "Include"));
    const box = h("div", "aconfirm", h("b", "disp", `Feed ${RARITY_LABEL[Rarity.Epic]} and ${RARITY_LABEL[Rarity.Legendary]} spares?`),
      h("p", "", "They melt for good. Fusion can turn three of them into one of the next rarity instead."), h("div", "acts", keep, go));
    keep.addEventListener("click", () => { confirming = false; box.remove(); });
    go.addEventListener("click", () => { confirming = false; box.remove(); epic = true; render(); });
    root.append(box);
  }

  function doFeed(t: GearItem): void {
    const from = t.level;
    const r = feedItems(profile, t.id, [...picked], epic);
    if (typeof r === "string") { toast(ctx.toastParent, refusal(r)); render(); return; }
    closeUndo();
    audio.play(r.toLevel - from > 1 ? "towerUpgradeMax" : "towerUpgrade");
    ctx.changed();
    refreshPanels(ctx, root);
    panel.close();
    toast(ctx.toastParent, `Fed ${r.fodder.length} spare${r.fodder.length === 1 ? "" : "s"}: Lv ${from} → ${r.toLevel}${r.gold > 0 ? ` · +${r.gold} gold` : ""}`);
    onFed({ from, to: r.toLevel });
  }

  onRefresh(panel, render);
  render();
}
